import { useState } from "react";
import { CheckCircle, Pencil, Save, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const DOELEN = [
  { key: "afvallen",       label: "Afvallen",               emoji: "🔥" },
  { key: "spieropbouw",    label: "Spieropbouw",             emoji: "💪" },
  { key: "sterker_worden", label: "Sterker worden",          emoji: "🏋️" },
  { key: "conditie",       label: "Conditie verbeteren",     emoji: "🏃" },
  { key: "onderhoud",      label: "Onderhoud / Fit blijven", emoji: "⚡" },
];

const NIVEAUS = [
  { key: "beginner",  label: "Beginner",  sub: "0–1 jaar" },
  { key: "gevorderd", label: "Gevorderd", sub: "1–3 jaar" },
  { key: "advanced",  label: "Advanced",  sub: "3+ jaar" },
];

const DOEL_LABEL = Object.fromEntries(DOELEN.map(d => [d.key, `${d.emoji} ${d.label}`]));
const NIVEAU_LABEL = Object.fromEntries(NIVEAUS.map(n => [n.key, n.label]));

export default function ProfielBewerkenSection({ user }) {
  const { loginWithToken } = useAuth();
  const token = user?.token;
  const profiel = user?.profile || {};

  const [bewerkModus, setBewerkModus] = useState(false);
  const [form, setForm] = useState({
    doel:     profiel.doel     || "",
    niveau:   profiel.niveau   || "",
    leeftijd: profiel.leeftijd || "",
    lengte:   profiel.lengte   || "",
    gewicht:  profiel.gewicht  || "",
  });
  const [loading, setLoading] = useState(false);
  const [succes,  setSucces]  = useState(false);
  const [fout,    setFout]    = useState("");

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleOpslaan = async () => {
    setLoading(true); setFout(""); setSucces(false);
    try {
      const r = await fetch(`${API}/api/me/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          doel:     form.doel     || null,
          niveau:   form.niveau   || null,
          leeftijd: form.leeftijd ? parseInt(form.leeftijd)    : null,
          lengte:   form.lengte   ? parseFloat(form.lengte)    : null,
          gewicht:  form.gewicht  ? parseFloat(form.gewicht)   : null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");

      // Ververs auth context met bijgewerkt profiel
      const me = await fetch(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json());
      loginWithToken({ ...me }, token);

      setSucces(true);
      setBewerkModus(false);
      setTimeout(() => setSucces(false), 3000);
    } catch (e) {
      setFout(e.message);
    } finally { setLoading(false); }
  };

  const heeftProfiel = profiel.doel || profiel.niveau || profiel.leeftijd;

  return (
    <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl overflow-hidden" data-testid="profiel-sectie">

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div>
          <p className="text-white font-bold text-sm">Trainingsprofiel</p>
          <p className="text-zinc-600 text-xs mt-0.5">Doel, niveau en lichaamsgegevens</p>
        </div>
        {!bewerkModus ? (
          <button onClick={() => setBewerkModus(true)} data-testid="bewerk-profiel-btn"
            className="flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-white border border-white/8 hover:border-white/20 rounded-lg px-3 py-1.5 transition-all">
            <Pencil size={12} /> Bewerken
          </button>
        ) : (
          <button onClick={() => { setBewerkModus(false); setFout(""); }} data-testid="annuleer-profiel-btn"
            className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-white border border-white/8 rounded-lg px-3 py-1.5 transition-all">
            <X size={12} /> Annuleren
          </button>
        )}
      </div>

      <div className="px-5 py-4 space-y-5">

        {/* Weergave modus */}
        {!bewerkModus && (
          <>
            {heeftProfiel ? (
              <div className="grid grid-cols-2 gap-3">
                {profiel.doel && (
                  <Stat label="Doel" value={DOEL_LABEL[profiel.doel] || profiel.doel} />
                )}
                {profiel.niveau && (
                  <Stat label="Niveau" value={NIVEAU_LABEL[profiel.niveau] || profiel.niveau} />
                )}
                {profiel.leeftijd && (
                  <Stat label="Leeftijd" value={`${profiel.leeftijd} jaar`} />
                )}
                {profiel.lengte && (
                  <Stat label="Lengte" value={`${profiel.lengte} cm`} />
                )}
                {profiel.gewicht && (
                  <Stat label="Gewicht" value={`${profiel.gewicht} kg`} />
                )}
              </div>
            ) : (
              <p className="text-zinc-600 text-sm text-center py-3">
                Nog geen profiel ingevuld.{" "}
                <button onClick={() => setBewerkModus(true)} className="text-[#C9A84C] hover:underline">
                  Vul het in →
                </button>
              </p>
            )}
          </>
        )}

        {/* Bewerkingsmodus */}
        {bewerkModus && (
          <div className="space-y-4">

            {/* Doel */}
            <div>
              <label className="block text-zinc-400 text-xs font-semibold mb-2 uppercase tracking-wide">Trainingsdoel</label>
              <div className="grid grid-cols-2 gap-2">
                {DOELEN.map(({ key, label, emoji }) => (
                  <button key={key} type="button" onClick={() => set("doel", key)}
                    data-testid={`profiel-doel-${key}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all text-left ${
                      form.doel === key
                        ? "border-[#C9A84C] bg-[#C9A84C]/10 text-white"
                        : "border-white/8 bg-white/2 text-zinc-500 hover:border-white/20 hover:text-white"
                    }`}>
                    <span>{emoji}</span> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Niveau */}
            <div>
              <label className="block text-zinc-400 text-xs font-semibold mb-2 uppercase tracking-wide">Niveau</label>
              <div className="grid grid-cols-3 gap-2">
                {NIVEAUS.map(({ key, label, sub }) => (
                  <button key={key} type="button" onClick={() => set("niveau", key)}
                    data-testid={`profiel-niveau-${key}`}
                    className={`flex flex-col items-center py-2.5 px-2 rounded-xl border text-center transition-all ${
                      form.niveau === key
                        ? "border-[#C9A84C] bg-[#C9A84C]/10 text-white"
                        : "border-white/8 bg-white/2 text-zinc-500 hover:border-white/20 hover:text-white"
                    }`}>
                    <span className="text-sm font-bold">{label}</span>
                    <span className="text-[10px] opacity-50 mt-0.5">{sub}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Lichaam stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { k: "leeftijd", label: "Leeftijd", unit: "jr",  placeholder: "25" },
                { k: "lengte",   label: "Lengte",   unit: "cm",  placeholder: "175" },
                { k: "gewicht",  label: "Gewicht",  unit: "kg",  placeholder: "75" },
              ].map(({ k, label, unit, placeholder }) => (
                <div key={k}>
                  <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">{label}</label>
                  <div className="relative">
                    <input type="number" value={form[k]} onChange={e => set(k, e.target.value)}
                      placeholder={placeholder} data-testid={`profiel-${k}`}
                      className="w-full bg-[#111] border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white placeholder-zinc-700 focus:outline-none focus:border-[#C9A84C]/40 pr-8 transition-colors" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 text-xs">{unit}</span>
                  </div>
                </div>
              ))}
            </div>

            {fout && (
              <p className="text-red-400 text-xs bg-red-900/20 border border-red-500/25 rounded-xl px-3 py-2">{fout}</p>
            )}

            <button onClick={handleOpslaan} disabled={loading} data-testid="opslaan-profiel-btn"
              className="w-full py-3 rounded-xl font-bold text-sm text-black flex items-center justify-center gap-2 disabled:opacity-60 transition-opacity"
              style={{ background: "#C9A84C" }}>
              {loading
                ? "Opslaan..."
                : <><Save size={14} /> Wijzigingen opslaan</>
              }
            </button>
          </div>
        )}

        {succes && (
          <div className="flex items-center gap-2 text-emerald-400 text-sm bg-emerald-500/8 border border-emerald-500/20 rounded-xl px-3 py-2" data-testid="profiel-succes">
            <CheckCircle size={14} /> Profiel opgeslagen!
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/3 rounded-xl px-4 py-3 border border-white/5">
      <p className="text-zinc-600 text-xs font-semibold uppercase tracking-wide mb-1">{label}</p>
      <p className="text-white text-sm font-bold">{value}</p>
    </div>
  );
}
